import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ExportFormat } from '../shared/enums/data-view.enum';

import { DataViewPromoteDialogComponent } from './data-view-promote-dialog/data-view-promote-dialog.component';
import { FwDialogConfirmComponent, FwDialogService } from '@flywheel-io/vision';
import { ApiParams, CoreApiService } from '../core-api.service';
import { LoggingService } from '../logging.service';
import { NotificationService } from '../notification.service';
import { ProfileService } from '../profile/profile.service';
import { downloadFile, downloadFileBlob } from '../shared/browser.utils';
import { DataView, DataViewExecution, DataViewPayload } from '../shared/models/data-view.model';
import { DownloadTicket } from '../shared/models/download.model';
import { Paginated } from '../shared/models/paginated.model';
import { Router } from '@angular/router';

export enum DataViewAction {
  Edit = 'edit',
  Copy = 'copy',
  CopyToProject = 'copyToProject',
  Delete = 'delete',
  Promote = 'promote',
  View = 'view',
}

@Injectable()
export class DataViewsService {
  private dialog = inject(FwDialogService);
  private coreApi = inject(CoreApiService);
  private http = inject(HttpClient);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private profileService = inject(ProfileService);
  private router = inject(Router);

  hasProjectDataViews$ = new BehaviorSubject<boolean>(false)
  hasSharedDataViews$ = new BehaviorSubject<boolean>(false)
  hasDataViewExecutions$ = new BehaviorSubject<boolean>(false)

  get isSiteContext(): boolean {
    return this.router.url.startsWith('/admin/dataviews');
  }

  isSharedDataView(dataView?: Partial<DataView>): boolean {
    return dataView?.parent === 'site';
  }

  fetchDataViews(containerId: string, params: ApiParams): Observable<Paginated<DataView[]>> {
    return this.profileService.exhaustiveMode$.pipe(
      switchMap((exhaustive) => {
        params.exhaustive = exhaustive;
        return this.coreApi.views.query(containerId, params).pipe(
          tap((resp) => {
            if (params.filter === 'parent!=site') {
              this.hasProjectDataViews$.next(resp.total > 0);
            } else if (resp.total > 0) {
              this.hasSharedDataViews$.next(resp.total > 0);
            }
          }),
        );
      }),
    );
  }

  fetchDataViewExecutions(containerId: string, params: ApiParams): Observable<Paginated<DataViewExecution[]>> {
    return this.profileService.exhaustiveMode$.pipe(
      switchMap((exhaustive) => {
        params.exhaustive = exhaustive;
        return this.coreApi.dataViewExecutions.query(params).pipe(
          tap((resp) => {
            this.hasDataViewExecutions$.next(resp.total > 0);
          }),
        );
      }),
    );
  }

  queueDataViewExecution(dataViewId: string, containerId: string): Observable<DataViewExecution> {
    return this.coreApi.views.queue.saved(dataViewId, containerId).pipe(
      tap(() => {
        this.notifications.success('views.queue.success');
      }),
      catchError((error) => {
        this.notifications.error('views.queue.error');
        this.logger.error(error);
        throw error;
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveDataView(id: string, viewData: DataViewPayload, action: 'update' | 'create'): Observable<any> {
    return (
      action === 'update' && this.isSiteContext
        ? (() => {
          const confirmDialog = this.dialog.openDialog(FwDialogConfirmComponent);
          confirmDialog.componentRef.setInput('contentText', 'Editing a site-wide data view template will update the template on each existing project. Do you wish to proceed?');
          confirmDialog.componentRef.setInput('confirmButtonText', 'Yes');
          confirmDialog.componentRef.setInput('cancelButtonText', 'No');
          confirmDialog.componentRef.setInput('width', 'small');
          return confirmDialog.closed;
        })()
        : of(true)
    ).pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.views[action](id, viewData)),
      tap(() => {
        this.notifications.success(this.isSharedDataView(viewData) ? `views.templates.${action}.success` : `views.${action}.success`);
      }),
      catchError((error) => {
        this.notifications.error(this.isSharedDataView(viewData) ? `views.templates.${action}.error` : `views.${action}.error`);
        throw error;
      }),
    );
  }

  exportViewData(containerId: string, dataViewId: string, dataViewLabel: string, format = 'csv'): Observable<DownloadTicket> {
    return this.coreApi.views.data.ticket(dataViewId, { containerId, format }).pipe(
      tap((ticket) => {
        const filename = `${dataViewLabel}.${format.includes('json') ? 'json' : format}`;
        downloadFile(
          dataViewLabel,
          `/api/views/${dataViewId}/data?ticket=${ticket.ticket}&filename=${filename}&containerId=${containerId}&format=${format}`,
        );
      }),
      catchError(error => {
        this.logger.error(error);
        this.notifications.error('views.export.error');
        throw error;
      }),
    );
  }

  exportExecutionData(dataViewExecutionId: string, dataViewExecutionLabel: string, format: ExportFormat = ExportFormat.CSV): Observable<DownloadTicket> {
    return this.coreApi.dataViewExecutions.data.ticket(dataViewExecutionId, format).pipe(
      switchMap((ticket) => {
        const extension = format.startsWith('json') ? 'json' : format;
        const filename = `${dataViewExecutionLabel}.${extension}`;
        const url = `/api/data_view_executions/${dataViewExecutionId}/data?ticket=${ticket.ticket}&file_format=${format}`;
        return this.http.get(url, { responseType: 'blob' }).pipe(
          tap((blob) => downloadFileBlob(filename, blob)),
          map(() => ticket),
        );
      }),
      catchError(error => {
        this.logger.error(error);
        this.notifications.error('views.export.error');
        throw error;
      }),
    );
  }

  deleteDataViewExecution(execution: DataViewExecution): Observable<void> {
    const confirmDialog = this.dialog.openDialog(FwDialogConfirmComponent);
    confirmDialog.componentRef.setInput('contentText', `Are you sure you want to delete data view execution labeled '${execution.data_view_label}'?`);
    confirmDialog.componentRef.setInput('confirmButtonText', 'Delete');
    confirmDialog.componentRef.setInput('confirmColor', 'danger');
    confirmDialog.componentRef.setInput('cancelButtonText', 'Cancel');
    confirmDialog.componentRef.setInput('width', 'small');
    return confirmDialog.closed.pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.dataViewExecutions.delete(execution._id)),
      tap(() => {
        this.notifications.success('views.delete.success');
      }),
      catchError(error => {
        this.notifications.error('views.delete.error');
        this.logger.error(error);
        throw error;
      }),
    );
  }

  deleteDataView(view: DataView): Observable<void> {
    const confirmDialog = this.dialog.openDialog(FwDialogConfirmComponent);
    confirmDialog.componentRef.setInput('contentText', this.isSiteContext
      ? `Are you sure you want to delete data view template labeled '${view.label}'?<br>Deleting a data view template will remove the data view from all projects.<br>Do you wish to proceed?`
      : `Are you sure you want to delete data view labeled '${view.label}'?`);
    confirmDialog.componentRef.setInput('confirmButtonText', 'Delete');
    confirmDialog.componentRef.setInput('confirmColor', 'danger');
    confirmDialog.componentRef.setInput('cancelButtonText', 'Cancel');
    confirmDialog.componentRef.setInput('width', 'small');
    return confirmDialog.closed.pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.views.delete(view._id)),
      tap(() => {
        this.notifications.success(this.isSiteContext ? 'views.templates.delete.success' : 'views.delete.success');
      }),
      catchError(error => {
        this.notifications.error(this.isSiteContext ? 'views.templates.delete.error' : 'views.delete.error');
        this.logger.error(error);
        throw error;
      }),
    );
  }

  promoteDataView(data: DataView): void {
    this.dialog.openDialog(DataViewPromoteDialogComponent, { data });
  }

  getActionTooltip(label: string, option: DataViewAction | DataViewAction[keyof DataViewAction]): string {
    switch (option) {
      case DataViewAction.Edit: {
        return `Modify '${label}'`;
      }
      case DataViewAction.Copy: {
        const target = this.isSiteContext ? 'shared data views' : 'current project';
        return `Copy '${label}' to ${target}`;
      }
      case DataViewAction.Delete: {
        const target = this.isSiteContext ? 'shared data views' : 'current project';
        return `Delete '${label}' from ${target}`;
      }
      case DataViewAction.Promote: {
        return `Promote '${label}' to shared data views`;
      }
      default: {
        return '';
      }
    }
  }
}
